import React, { useEffect, useState, useRef } from "react";
import { Link } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { Bell, CheckCheck } from "lucide-react";
import api from "@/lib/api";
import { notificationLink } from "@/lib/notificationLink";
import { useAuth } from "@/contexts/AuthContext";
import { useUnreadCount, setUnreadCount } from "@/hooks/unreadCounts";
import { formatDateTime } from "@/lib/dates";

export default function NotificationBell() {
  const { t } = useTranslation();
  const [open, setOpen] = useState(false);
  const [items, setItems] = useState([]);
  /* The count is not this component's own (T12-M1), it is the notifications
     CHANNEL's — one request shared by every subscriber, because two pollers
     would be two answers that disagree for up to 45 seconds. `setUnreadCount`
     is how this component, which sometimes knows sooner having just marked
     something read, corrects it.

     Since T14-M2 the Messages cell no longer reads this number: it counts
     messages, which is a different question. See hooks/unreadCounts.js. */
  const unread = useUnreadCount();
  const ref = useRef(null);
  /* Where the viewport-pinned panel starts, below `sm` (T14-M1). See the panel
     itself for why this is measured rather than written down. */
  const [panelTop, setPanelTop] = useState(60);
  const { user } = useAuth();
  const role = user?.profile?.role;

  // Moved to lib/notificationLink.js, and decided by the notification's TYPE
  // rather than by which related id it happens to carry. The old chain put the
  // thread first, and `quote_accepted` names a thread, so every accepted quote
  // opened the chat instead of the job. The helper is the mirror of the table
  // the push reads (backend/services/notify.py), so both channels lead to the
  // same page for the same row.
  const linkFor = (n) => notificationLink(n, role);

  const loadList = async () => {
    try {
      const { data } = await api.get("/notifications?limit=20");
      setItems(data.notifications || []);
      // The list carries a fresher count than the last poll did.
      setUnreadCount(data.unread || 0);
    } catch {}
  };

  useEffect(() => {
    const handler = (e) => {
      if (ref.current && !ref.current.contains(e.target)) setOpen(false);
    };
    document.addEventListener("mousedown", handler);
    /* `touchstart` as well (T14-M1). Until now this panel only ever opened over
       the admin console, which nobody runs on a phone; on iOS a tap on a plain
       element does not always synthesise `mousedown`, so "tap outside to close"
       was a desktop-only affordance. It has to work now that the bell is in
       every mobile header. `passive` because the handler never prevents the
       touch — it only decides whether to close. */
    document.addEventListener("touchstart", handler, { passive: true });
    return () => {
      document.removeEventListener("mousedown", handler);
      document.removeEventListener("touchstart", handler);
    };
  }, []);

  const toggle = () => {
    setOpen((v) => {
      if (!v) {
        loadList();
        // Measured at open, not on a timer: every header this bell sits in is
        // fixed or sticky at the top of the viewport, so the button does not
        // move under it while the panel is up.
        const rect = ref.current?.getBoundingClientRect();
        if (rect) setPanelTop(Math.round(rect.bottom) + 8);
      }
      return !v;
    });
  };

  const markAll = async () => {
    await api.patch("/notifications/read-all");
    setItems((prev) => prev.map((n) => ({ ...n, is_read: true })));
    setUnreadCount(0);
  };

  const onClickItem = async (n) => {
    if (!n.is_read) {
      await api.patch(`/notifications/${n.notification_id}/read`);
      setUnreadCount(unread - 1);
    }
    setOpen(false);
  };

  return (
    <div className="relative" ref={ref}>
      {/* The label is the panel's own title, and with a count it is the phrase
          the tab bar already uses for the same job — both keys exist in EN and
          ES, and T14 adds no copy. Icon-only, so without one a screen reader
          reads "button". */}
      <button
        onClick={toggle}
        aria-expanded={open}
        aria-haspopup="menu"
        aria-label={unread > 0
          ? t("nav.tabs.unread", { label: t("notif.bell.title"), count: unread })
          : t("notif.bell.title")}
        className="relative p-2 rounded-sm hover:bg-current/10 text-current"
        data-testid="notification-bell"
      >
        <Bell size={20} />
        {unread > 0 && (
          <span className="absolute top-0 right-0 min-w-[18px] h-[18px] bg-danger text-cream text-[10px] font-bold rounded-full flex items-center justify-center px-1" data-testid="unread-badge">
            {unread > 9 ? "9+" : unread}
          </span>
        )}
      </button>

      {/*
        The bell sits near the right edge of its header, so a right-anchored
        320px panel starts at a negative x on a phone and clips the first
        character of every line. Below sm the panel is pinned to the viewport —
        8px in from each edge, which is why it cannot overflow horizontally
        whatever the screen — instead of to the button; from sm up nothing
        changes.

        The vertical start used to be the literal 60px of the admin bar, the one
        mobile header this bell had (`p-3` around a 36px icon button). T14-M1
        gives it three more — the shell header at 64px, the Panel's section
        header at 56px, the dashboard's mark row at ~52 — so it is measured off
        the button instead of assumed. Same number as before under the admin
        bar; correct under the other three.

        `--bell-top` rather than an inline `top`, because an inline style would
        also beat `sm:top-full` and drag the desktop panel to the same place.
      */}
      {open && (
        <div style={{ "--bell-top": `${panelTop}px` }} className="fixed left-2 right-2 top-[var(--bell-top)] sm:absolute sm:left-auto sm:right-0 sm:top-full sm:mt-2 sm:w-80 bg-cream border border-sand rounded-md shadow-deep z-50 overflow-hidden" data-testid="notifications-dropdown">
          <div className="flex items-center justify-between p-3 border-b border-sand">
            <p className="font-display font-bold text-worker">{t("notif.bell.title")}</p>
            {unread > 0 && (
              <button onClick={markAll} className="text-xs text-smoke hover:text-worker flex items-center gap-1" data-testid="mark-all-read">
                <CheckCheck size={14} /> {t("notif.bell.markAll")}
              </button>
            )}
          </div>
          <div className="max-h-96 overflow-y-auto">
            {items.length === 0 ? (
              <p className="p-6 text-center text-smoke text-sm">{t("notif.bell.empty")}</p>
            ) : (
              items.map((n) => (
                <Link
                  key={n.notification_id}
                  to={linkFor(n)}
                  onClick={() => onClickItem(n)}
                  className={`block p-3 border-b border-sand last:border-0 hover:bg-linen ${n.is_read ? "" : "bg-hive/5"}`}
                  data-testid={`notif-${n.notification_id}`}
                >
                  <div className="flex items-start gap-2">
                    {!n.is_read && <span className="mt-1.5 w-2 h-2 rounded-full bg-hive shrink-0" />}
                    <div className="min-w-0">
                      <p className="font-display font-semibold text-sm text-worker break-words">{n.title}</p>
                      <p className="text-xs text-smoke mt-0.5 line-clamp-2 break-words">{n.body}</p>
                      <p className="text-[10px] text-smoke font-mono mt-1">{formatDateTime(n.created_at)}</p>
                    </div>
                  </div>
                </Link>
              ))
            )}
          </div>
        </div>
      )}
    </div>
  );
}
